/**
 * Teacher Profile Slice
 * Manages teacher profile state with cache
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getTeacherProfile,
  updateTeacherProfile as updateTeacherProfileApi,
  uploadProfilePhoto as uploadProfilePhotoApi,
} from "@/services/api/endpoints/teacher-profile.api";
import {
  TeacherProfile,
  TeacherProfileUpdatePayload,
} from "@/types/teacher-profile.types";
import { logger } from "@/utils/logger";

interface TeacherProfileState {
  data: TeacherProfile | null;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  uploadProgress: number;
}

const initialState: TeacherProfileState = {
  data: null,
  loading: false,
  error: null,
  lastFetched: null,
  uploadProgress: 0,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Fetch teacher profile with cache
 */
export const fetchTeacherProfile = createAsyncThunk(
  "teacherProfile/fetch",
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    logger.log("🔍 [teacherProfile] fetchTeacherProfile called", {
      forceRefresh,
    });
    try {
      const state = getState() as { teacherProfile: TeacherProfileState };
      const { lastFetched, data } = state.teacherProfile;

      logger.log("📦 [teacherProfile] Cache state:", {
        hasCachedData: !!data,
        lastFetched: lastFetched ? new Date(lastFetched).toISOString() : null,
        cacheAge: lastFetched ? Date.now() - lastFetched : null,
        cacheValid: lastFetched
          ? Date.now() - lastFetched < CACHE_DURATION
          : false,
      });

      // Check cache validity
      // Important: Check lastFetched first to avoid refetching when API returns undefined
      if (
        !forceRefresh &&
        lastFetched &&
        Date.now() - lastFetched < CACHE_DURATION
      ) {
        logger.log(
          "✅ [teacherProfile] Using cached data (or skipping if undefined)"
        );
        // If we recently fetched and got undefined, don't refetch immediately
        if (data) {
          return data;
        }
        // API returned undefined recently, throw error to stop loop
        throw new Error(
          "Profile endpoint returned undefined. Please check if the user profile exists in the database."
        );
      }

      // Fetch fresh data
      logger.log("🌐 [teacherProfile] Fetching from API...");
      const profile = await getTeacherProfile();
      logger.log("✅ [teacherProfile] Profile fetched:", profile);
      return profile;
    } catch (error: any) {
      logger.error("❌ [teacherProfile] Fetch error:", error);
      return rejectWithValue(error.message || "Failed to fetch profile");
    }
  }
);

/**
 * Update teacher profile
 */
export const updateTeacherProfile = createAsyncThunk(
  "teacherProfile/update",
  async (payload: TeacherProfileUpdatePayload, { rejectWithValue }) => {
    try {
      const profile = await updateTeacherProfileApi(payload);
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update profile");
    }
  }
);

/**
 * Upload profile photo
 */
export const uploadProfilePhoto = createAsyncThunk(
  "teacherProfile/uploadPhoto",
  async ({ file }: { file: File }, { rejectWithValue, dispatch }) => {
    try {
      const photoUrl = await uploadProfilePhotoApi(file, (progress) => {
        dispatch(setUploadProgress(progress));
      });
      return photoUrl;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload photo");
    }
  }
);

/**
 * Teacher Profile Slice
 */
const teacherProfileSlice = createSlice({
  name: "teacherProfile",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
    },
    clearProfile: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Profile
      .addCase(fetchTeacherProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeacherProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchTeacherProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Profile
      .addCase(updateTeacherProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTeacherProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        // Só atualiza lastFetched se tiver dados válidos
        if (action.payload) {
          state.lastFetched = Date.now();
        }
        state.error = null;
      })
      .addCase(updateTeacherProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Invalida cache em caso de erro
        state.lastFetched = null;
      })

      // Upload Photo
      .addCase(uploadProfilePhoto.pending, (state) => {
        state.error = null;
      })
      .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
        // If profile doesn't exist yet, create a minimal one with the photo
        if (!state.data) {
          state.data = {
            email: "",
            name: "",
            photo: action.payload,
          };
        } else {
          state.data.photo = action.payload;
        }
        state.uploadProgress = 0;
        state.error = null;
        state.lastFetched = Date.now(); // Update cache timestamp
      })
      .addCase(uploadProfilePhoto.rejected, (state, action) => {
        state.error = action.payload as string;
        state.uploadProgress = 0;
      });
  },
});

export const {
  clearError,
  setUploadProgress,
  resetUploadProgress,
  clearProfile,
} = teacherProfileSlice.actions;
export default teacherProfileSlice.reducer;
